import { CheckCircle, ChevronDown, Key, XCircle } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleAdsAddKeywords } from 'sprites-types/workflow/tools/googleAdsAddKeywords'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function KeywordAddItem({ keyword, className }: { keyword: any; className?: string }) {
  const getStatusColor = (success: boolean) => {
    return success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }

  const getMatchTypeDisplay = (matchType?: string) => {
    if (!matchType) return null
    const matchTypeMap: Record<string, string> = {
      EXACT: 'Exact Match',
      PHRASE: 'Phrase Match',
      BROAD: 'Broad Match',
    }
    return matchTypeMap[matchType] || matchType
  }

  const getMatchTypeBadgeColor = (matchType?: string) => {
    switch (matchType) {
      case 'EXACT':
        return 'bg-blue-100 text-blue-700'
      case 'PHRASE':
        return 'bg-purple-100 text-purple-700'
      case 'BROAD':
        return 'bg-orange-100 text-orange-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const isNegative = keyword.negative || false
  const iconColor = isNegative ? 'text-red-600' : 'text-blue-600'
  const IconComponent = isNegative ? XCircle : Key
  const titleText = isNegative ? 'Negative Keyword Added' : 'Keyword Added'
  const keywordText = keyword.keyword
  const matchType = keyword.matchType

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
          <IconComponent className={`h-4 w-4 ${iconColor}`} />
          <div className="flex-1 text-sm font-semibold text-gray-900">{titleText}</div>
          <div className={`rounded-full px-2 py-1 text-xs ${getStatusColor(keyword.success)}`}>
            {keyword.success ? 'Success' : 'Failed'}
          </div>
        </div>

        {/* Keyword text and match type */}
        {keywordText && (
          <div className="mb-3 space-y-1.5">
            <div className="text-xs text-gray-600">
              Keyword: <span className="font-medium text-gray-900">{keywordText}</span>
            </div>
            {matchType && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Match Type:</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMatchTypeBadgeColor(matchType)}`}
                >
                  {getMatchTypeDisplay(matchType)}
                </span>
              </div>
            )}
            {keyword.cpcBidMicros && (
              <div className="text-xs text-gray-600">
                CPC Bid:{' '}
                <span className="font-medium text-gray-900">
                  ${(keyword.cpcBidMicros / 1000000).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        {keyword.success && keyword.criterionId && (
          <div className="mb-3 space-y-1">
            <div className="text-xs text-gray-600">
              Criterion ID: <span className="font-medium text-gray-900">{keyword.criterionId}</span>
            </div>
            {keyword.resourceName && (
              <div className="text-xs text-gray-600">
                Resource: <span className="font-mono text-[10px]">{keyword.resourceName}</span>
              </div>
            )}
          </div>
        )}

        {keyword.error && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="text-xs text-red-600">{keyword.error}</div>
          </div>
        )}

        {keyword.success && (
          <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-2">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="text-xs text-green-600">
              {keyword.negative
                ? 'Negative keyword successfully added and will prevent ads from showing for this term'
                : 'Keyword successfully added and ready to trigger ads'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleAdsAddKeywordsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsAddKeywords, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  // Handle both emitMode: 'single' (result is object with keywords array) and 'multi' (result is array of objects)
  const keywords = Array.isArray(result)
    ? result.flatMap((r) => r.keywords || [])
    : result.keywords || []

  // Determine if this is a negative keyword operation (check results first, then fallback to inputs)
  const inputs = nodeExecution.inputs as any
  const isNegative =
    keywords.length > 0 && keywords[0]?.negative !== undefined
      ? keywords[0].negative
      : inputs?.negative ||
        inputs?.args?.negative ||
        inputs?.functionArgs?.action?.params?.negative ||
        inputs?.functionArgs?.negative ||
        false

  if (keywords.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          {isNegative ? (
            <XCircle className="h-5 w-5 text-gray-400" />
          ) : (
            <Key className="h-5 w-5 text-gray-400" />
          )}
          <div className="text-sm text-gray-600">
            {isNegative ? 'No negative keywords added' : 'No keywords added'}
          </div>
        </div>
      </div>
    )
  }

  if (keywords.length === 1) {
    return <KeywordAddItem keyword={keywords[0]} />
  }

  const successfulKeywords = keywords.filter((kw: any) => kw.success).length

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              {isNegative ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : (
                <Key className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                {isNegative ? 'Negative Keyword Addition Results' : 'Keyword Addition Results'}
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {successfulKeywords} of {keywords.length}{' '}
                {isNegative ? 'negative keywords' : 'keywords'} added successfully
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {keywords.map((keyword: any, index: number) => {
            return (
              <KeywordAddItem
                key={keyword.criterionId || index}
                keyword={keyword}
                className="mx-8"
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsAddKeywords(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsAddKeywordsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsAddKeywords, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  // Handle both emitMode: 'single' (result is object with keywords array) and 'multi' (result is array of objects)
  const keywords = Array.isArray(result)
    ? result.flatMap((r) => r.keywords || [])
    : result.keywords || []

  if (keywords.length === 0) {
    return `## Google Ads Add Keywords Results

No keywords were added.

---
`
  }

  const keywordsMarkdown = keywords
    .map((keyword: any) => {
      return `**Success:** ${keyword.success ? 'Yes' : 'No'}
${keyword.keyword ? `**Keyword:** ${keyword.keyword}` : ''}
${keyword.matchType ? `**Match Type:** ${keyword.matchType}` : ''}
${keyword.negative !== undefined ? `**Negative:** ${keyword.negative ? 'Yes' : 'No'}` : ''}
${keyword.cpcBidMicros ? `**CPC Bid:** $${(keyword.cpcBidMicros / 1000000).toFixed(2)}` : ''}
${keyword.criterionId ? `**Criterion ID:** ${keyword.criterionId}` : ''}
${keyword.resourceName ? `**Resource Name:** ${keyword.resourceName}` : ''}
${keyword.error ? `**Error:** ${keyword.error}` : ''}`
    })
    .join('\n\n---\n\n')

  return `## Google Ads Add Keywords Results (${keywords.length} keywords)

${keywordsMarkdown}

---
`
}

export const GoogleAdsAddKeywordsDisplay = createWorkflowToolComponent(
  GoogleAdsAddKeywordsDisplayComponent,
  downloadGoogleAdsAddKeywords,
  googleAdsAddKeywordsToMarkdown
)
